/**
 * Community — Helpful on an owner review (phase 2): the pure state of one control and the
 * reader's own-review rule. No fetch, no React; unit-tested in `tests/community-helpful.spec.ts`.
 *
 * Whose figure is what:
 * - the COUNT is DealHunt's public `helpful_count` (shared-cached, in the HTML) until the
 *   reader votes; from then on it is the count DealHunt answered the vote with — never our +1;
 * - the reader's OWN vote (`mine`) is never in the HTML. DealHunt answers it only on the keyed
 *   routes: `GET …/helpful/me` (read on the thread page), the batched `GET …/helpful/me?note_ids=`
 *   (lists, `useHelpfulMine`) and the answer of a vote. Until one lands the vote is UNKNOWN
 *   (`known: false`, shown not pressed): a press then asks PUT (idempotent — on a review the
 *   reader already marked it answers `mine: true`) and changes NO count until DealHunt answers;
 *   only a known state shows an optimistic ±1 (the Helpful button, `helpfulPress`).
 * - the feed's Yes / No (2026-10-18): the PRESSED LOOK is optimistic, the figures never are — a
 *   tap shows its side at once (an unknown vote through `pending` while `busy`, a known one by
 *   moving `value`), and the counts stay until DealHunt's answer — or a rollback — replaces them.
 *
 * The feed's Yes / No vote ("Did this help you decide?", contract #199–#202) is the same row on
 * DealHunt with a value: `count` stays the Yes count (= `helpful_count`), `no` the No count, and
 * `value` the reader's vote (`mine` = "my Yes is on it", as DealHunt answers it). The same rules
 * hold: counts from the HTML until a vote answers, then DealHunt's figures; the reader's own value
 * only from a keyed read or a vote's answer, never the shared HTML.
 */
import type { CommunityHelpfulAnswer, CommunityVoteCounts, CommunityVoteValue } from './types'

export interface HelpfulState {
  /** Yes votes — Helpful. */
  count: number
  /** No votes (the feed's Yes / No control); 0 where DealHunt sends none. */
  no: number
  /** The reader's Yes stands. false until known. */
  mine: boolean
  /** The reader's vote: Yes, No, or none — null until known. `mine` === (`value` === 'yes'). */
  value: CommunityVoteValue | null
  /** A vote's answer has landed: from now on it is the truth, and a read of `mine` asked before
   *  the vote must not overwrite it. */
  settled: boolean
  /** The reader's vote is known — a read (`/helpful/me`, single or batched), a remembered answer
   *  or a vote's answer landed. While false a press shows no optimistic count. */
  known: boolean
  /** A vote is in flight: further presses are ignored. */
  busy: boolean
  /** The feed's Yes / No only: the side tapped on an UNKNOWN vote, shown pressed while the vote is
   *  in flight (`votePressed`). No count moves with it. null whenever nothing is in flight. */
  pending: CommunityVoteValue | null
  /** The reader wrote this review — DealHunt refuses the author's own vote (`own_note`). */
  own: boolean
}

export function helpfulInitial(count: number, no = 0): HelpfulState {
  return {
    count: Math.max(0, count),
    no: Math.max(0, no),
    mine: false,
    value: null,
    settled: false,
    known: false,
    busy: false,
    pending: null,
    own: false,
  }
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
}

/** `{ yes, no }`, both counts — or not vote counts at all. */
export function isVoteCounts(value: unknown): value is CommunityVoteCounts {
  const v = value as Partial<CommunityVoteCounts> | null
  return typeof v === 'object' && v !== null && isCount(v.yes) && isCount(v.no)
}

/** A `{ helpful, mine }` that can be shown: every number is DealHunt's, so a body without a
 *  finite count or a boolean `mine` (a proxy page, an older build, `{}`) is no answer at all.
 *  The vote's fields (#199) are optional — an older DealHunt answers without `value` and
 *  `vote_counts` — but when sent they must be well-formed: a `value` that is not `"yes"`, `"no"` or
 *  null, or `vote_counts` that are not two counts, make it no answer either. */
export function isHelpfulAnswer(value: unknown): value is CommunityHelpfulAnswer {
  const v = value as Partial<CommunityHelpfulAnswer> | null
  return (
    typeof v === 'object' &&
    v !== null &&
    isCount(v.helpful) &&
    typeof v.mine === 'boolean' &&
    (v.value === undefined || v.value === null || v.value === 'yes' || v.value === 'no') &&
    (v.vote_counts === undefined || isVoteCounts(v.vote_counts))
  )
}

/** The reader's vote in an answer: DealHunt's `value`, or — an older DealHunt, which knows only
 *  Helpful — Yes when `mine`, else none. */
export function helpfulValue(answer: CommunityHelpfulAnswer): CommunityVoteValue | null {
  if (answer.value !== undefined) {
    return answer.value
  }
  return answer.mine ? 'yes' : null
}

/** An answer's figures as state fields: the Yes count is `helpful`; the No count is the
 *  answer's when it sends one, else `no` (what the control showed). */
function answerFields(
  answer: CommunityHelpfulAnswer,
  no: number
): Pick<HelpfulState, 'count' | 'no' | 'mine' | 'value'> {
  const value = helpfulValue(answer)
  return {
    count: Math.max(0, answer.helpful),
    no: answer.vote_counts ? answer.vote_counts.no : no,
    mine: value === 'yes',
    value,
  }
}

/**
 * The control's first state: the public count, or — when this page already got DealHunt's
 * answer for the review (a feed row remounted by a tab switch) — that answer, settled.
 */
export function helpfulSeed(
  count: number,
  remembered: CommunityHelpfulAnswer | null | undefined,
  no = 0
): HelpfulState {
  return isHelpfulAnswer(remembered)
    ? {
        ...helpfulInitial(count, no),
        ...answerFields(remembered, no),
        settled: true,
        known: true,
      }
    : helpfulInitial(count, no)
}

/** The reader wrote this review (`/me`, `isOwnReview`): the control turns inert. */
export function helpfulOwn(state: HelpfulState, own: boolean): HelpfulState {
  return own && !state.own
    ? { ...state, own: true, mine: false, value: null, pending: null }
    : state
}

/**
 * The keyed read of the reader's state (`GET …/helpful/me`), applied unless a vote already
 * answered or is in flight: a read asked before the press can resolve after it, and the vote's
 * answer is newer. Its count is DealHunt's live figure — fresher than the cached HTML's.
 */
export function helpfulRead(state: HelpfulState, answer: unknown): HelpfulState {
  if (state.settled || state.busy || !isHelpfulAnswer(answer)) {
    return state
  }
  const fields = answerFields(answer, state.no)
  return {
    ...state,
    ...fields,
    mine: !state.own && fields.mine,
    value: state.own ? null : fields.value,
    known: true,
  }
}

/** The state with the reader's vote moved to `value`, its counts moved with it (optimistic). */
function withVote(state: HelpfulState, value: CommunityVoteValue | null): HelpfulState {
  const yes = (value === 'yes' ? 1 : 0) - (state.value === 'yes' ? 1 : 0)
  const no = (value === 'no' ? 1 : 0) - (state.value === 'no' ? 1 : 0)
  return {
    ...state,
    value,
    mine: value === 'yes',
    count: Math.max(0, state.count + yes),
    no: Math.max(0, state.no + no),
  }
}

/**
 * A press: what to ask (`on` = mark Helpful, false = undo) and the state to show while it is
 * asked. Known vote: optimistic (`mine` flips, count ±1). Unknown vote: `on` (an idempotent PUT)
 * and only `busy` — the count and `mine` wait for DealHunt's answer, which may say the reader had
 * already marked it. null = nothing to do (a vote in flight, or the reader's own review).
 */
export function helpfulPress(state: HelpfulState): { on: boolean; next: HelpfulState } | null {
  if (state.busy || state.own) {
    return null
  }
  const on = !state.mine
  if (!state.known) {
    return { on, next: { ...state, busy: true } }
  }
  return { on, next: { ...withVote(state, on ? 'yes' : null), busy: true } }
}

/**
 * A tap on the feed's Yes or No: what to ask (`value` — `"yes"` / `"no"` = PUT that value, null =
 * DELETE, the un-vote) and the state to show meanwhile. Known vote: tapping the side already
 * chosen takes the vote back, the other side switches it — the pressed side moves at once, the
 * counts do not (they are DealHunt's: its answer brings them). Unknown
 * vote: always the tapped side (an idempotent PUT: the reader may already have voted it, and an
 * un-vote must never be sent blind), `busy` and the tapped side shown pressed (`pending`) — the
 * counts wait for DealHunt's answer (876f5159: the look is optimistic, the figures are not).
 * null = nothing to do (a vote in flight, or the reader's own review).
 */
export function votePress(
  state: HelpfulState,
  choice: CommunityVoteValue
): { value: CommunityVoteValue | null; next: HelpfulState } | null {
  if (state.busy || state.own) {
    return null
  }
  if (!state.known) {
    return { value: choice, next: { ...state, busy: true, pending: choice } }
  }
  const value = state.value === choice ? null : choice
  return { value, next: { ...state, value, mine: value === 'yes', busy: true } }
}

/** A vote's PUT body as the proxy reads it (contract #199): absent (or null) = `"yes"` — the
 *  Helpful button sends `{}` —, `"yes"` / `"no"` as sent; anything else, or a body that is not a
 *  JSON object (no `Content-Type: application/json`), = undefined: refused (`invalid_vote`),
 *  never forwarded as a Yes. */
export function parseVoteValue(body: unknown): CommunityVoteValue | undefined {
  if (body === undefined || body === null || body === '') {
    return 'yes'
  }
  if (typeof body !== 'object' || Array.isArray(body)) {
    return undefined
  }
  const raw = (body as Record<string, unknown>).value
  if (raw === undefined || raw === null) {
    return 'yes'
  }
  return raw === 'yes' || raw === 'no' ? raw : undefined
}

/** The side the feed's Yes / No shows pressed: the tapped one while an unknown vote is in flight
 *  (`pending`), else the reader's known vote. */
export function votePressed(
  state: Pick<HelpfulState, 'pending' | 'value'>
): CommunityVoteValue | null {
  return state.pending ?? state.value
}

/**
 * The voter changed under a mounted control — the session token arrived, changed or went
 * (sign-in, account switch, logout). What the control knew was the previous voter's:
 * - signed in: the state is no longer `settled`, so the keyed read under the new bearer
 *   (`useHelpfulMine` re-reads per token; DealHunt answers the member's vote, else the folded or
 *   still-unclaimed device's) replaces it. The shown vote stays until that read lands — no flicker.
 * - signed out: the reader's vote goes from the screen too (#114, 2026-09-13: nothing of the
 *   reader stays after Logout) — unknown again, the counts stay the last ones DealHunt answered.
 * Ignored while a vote is in flight: its answer is about to land for the voter who sent it.
 */
export function helpfulVoterChanged(state: HelpfulState, signedIn: boolean): HelpfulState {
  if (state.busy) {
    return state
  }
  if (signedIn) {
    return state.settled ? { ...state, settled: false } : state
  }
  return { ...state, settled: false, known: false, mine: false, value: null }
}

/** The vote's question on the feed row (plan §2). */
export const VOTE_QUESTION = 'Did this help you decide?'

/** The public phrase beside Yes / No: "Be the first to say" at 0 votes only; from the first vote
 *  the split, "1 of 1 found this helpful", "0 of 1 …", "13 of 16 …" (UX review 2026-10-18 — at one
 *  or two votes "Be the first to say" was false). The figures are DealHunt's, never optimistic. */
export function votePhrase(state: Pick<HelpfulState, 'count' | 'no'>): string {
  const total = state.count + state.no
  if (total === 0) {
    return 'Be the first to say'
  }
  return `${state.count.toLocaleString('en-US')} of ${total.toLocaleString(
    'en-US'
  )} found this helpful`
}

/** The author's own card: the full split, whatever the total (plan §2) — "13 yes · 3 no". */
export function voteBreakdown(state: Pick<HelpfulState, 'count' | 'no'>): string {
  return `${state.count.toLocaleString('en-US')} yes · ${state.no.toLocaleString('en-US')} no`
}

/**
 * The vote a signed-out press asked for, replayed once the sign-in lands (2026-10-09). Always a
 * MARK (`on: true`), never `helpfulPress`'s toggle: the thread's `/helpful/me` read starts with
 * the same session and may land first saying the reader already marked it — a toggle would then
 * undo their vote. null = nothing to send: already marked, the reader's own review, or a vote in
 * flight. Unknown vote: only `busy` (the PUT is idempotent; DealHunt's answer sets the count).
 */
export function helpfulReplay(state: HelpfulState): { on: true; next: HelpfulState } | null {
  if (state.mine || state.own || state.busy) {
    return null
  }
  const step = helpfulPress(state)
  return step && step.on ? { on: true, next: step.next } : null
}

/** A press waiting for the sign-in it opened, with the session token it was refused under (null
 *  signed out; the rejected one after `auth_invalid`). null = nothing waiting. */
export type HelpfulPending = { token: string | null } | null

/** Replay the waiting press now: a session token arrived that is not the one it was refused under. */
export function helpfulReplayDue(pending: HelpfulPending, token: string | null): boolean {
  return pending !== null && token !== null && pending.token !== token
}

/** The sign-in dialog closed (after the grace): still the token the press was refused under →
 *  the reader dismissed it, the press is dropped; a new token keeps it for the replay. */
export function helpfulPendingAfterDismiss(
  pending: HelpfulPending,
  token: string | null
): HelpfulPending {
  return pending !== null && token === pending.token ? null : pending
}

/** DealHunt's answer replaces the optimistic figures. null = the body is not an answer
 *  (`isHelpfulAnswer`): the caller rolls the press back — our +1 is never left standing. */
export function helpfulAnswer(state: HelpfulState, answer: unknown): HelpfulState | null {
  if (!isHelpfulAnswer(answer)) {
    return null
  }
  return {
    ...state,
    ...answerFields(answer, state.no),
    busy: false,
    pending: null,
    settled: true,
    known: true,
  }
}

/** The most review ids one batched `/helpful/me` read asks for (DealHunt's cap). */
export const HELPFUL_BATCH_MAX = 50

/** The ids to read in batches: deduped, sorted (a stable key for the same set), chunked at
 *  `HELPFUL_BATCH_MAX`. */
export function helpfulBatches(noteIds: readonly string[]): string[][] {
  const ids = Array.from(new Set(noteIds.filter(Boolean))).sort()
  const out: string[][] = []
  for (let i = 0; i < ids.length; i += HELPFUL_BATCH_MAX) {
    out.push(ids.slice(i, i + HELPFUL_BATCH_MAX))
  }
  return out
}

/** The answers in a batched read's body (`{ votes: { [noteId]: { helpful, mine, value,
 *  vote_counts } } }`): only entries that are answers (`isHelpfulAnswer`), the vote's fields
 *  carried when DealHunt sends them. Anything else — an older DealHunt, a proxy page — is an
 *  empty map: unknown, never "not pressed". */
export function helpfulVotes(body: unknown): Map<string, CommunityHelpfulAnswer> {
  const out = new Map<string, CommunityHelpfulAnswer>()
  const votes = (body as { votes?: unknown } | null)?.votes
  if (typeof votes !== 'object' || votes === null || Array.isArray(votes)) {
    return out
  }
  for (const [id, answer] of Object.entries(votes as Record<string, unknown>)) {
    if (isHelpfulAnswer(answer)) {
      out.set(id, {
        helpful: answer.helpful,
        mine: answer.mine,
        ...(answer.value !== undefined ? { value: answer.value } : {}),
        ...(answer.vote_counts
          ? { vote_counts: { yes: answer.vote_counts.yes, no: answer.vote_counts.no } }
          : {}),
      })
    }
  }
  return out
}

/** DealHunt's sentence for a sign-in that cannot vote: only a Google sign-in carries the subject
 *  a vote is keyed on (Reddit / X give no verified email, and DealHunt answers
 *  `identity_unsupported` for any non-Google subject). */
export const HELPFUL_GOOGLE_ONLY = 'Sign in with Google to mark owner reviews as helpful.'

/** The same sentence on a discussion (`HelpfulTarget.googleOnly`): a question's vote is
 *  "Same question", a discussion's is Helpful — never "owner reviews". */
export function discussionGoogleOnly(kind: 'question' | 'discussion'): string {
  return kind === 'question'
    ? 'Sign in with Google to mark "Same question".'
    : 'Sign in with Google to mark discussions as helpful.'
}

const GOOGLE_ONLY_CODES: ReadonlySet<string> = new Set([
  'email_required',
  'email_unverified',
  'identity_unsupported',
])

/** What a refused vote says: the Google-only sentence for an identity that cannot vote (our
 *  identity check's `email_required` names an email, which a Reddit or X reader cannot fix) —
 *  worded for what is voted on (`googleOnly`, default the owner review's) — else DealHunt's
 *  message, else ours. */
export function helpfulErrorMessage(
  code: string,
  message: string | null | undefined,
  googleOnly: string = HELPFUL_GOOGLE_ONLY
): string {
  if (GOOGLE_ONLY_CODES.has(code)) {
    return googleOnly
  }
  return message || 'Could not save that — try again in a moment.'
}

/** The vote failed: back to what was shown before the press. `own` = DealHunt said `own_note`
 *  (the author on a browser that could not tell) — the control turns into the author's. */
export function helpfulRollback(
  before: HelpfulState,
  state: HelpfulState,
  own = false
): HelpfulState {
  return {
    ...state,
    busy: false,
    pending: null,
    count: before.count,
    no: before.no,
    mine: own ? false : before.mine,
    value: own ? null : before.value,
    own: state.own || own,
  }
}

/** What the button says beside its count — "Helpful", or a discussion's "Same question". */
export function helpfulText(state: Pick<HelpfulState, 'count'>, label = 'Helpful'): string {
  return state.count > 0 ? `${label} · ${state.count.toLocaleString('en-US')}` : label
}

// ── discussions (phase 3): the same control on a question or discussion ─────────────────────

/**
 * A discussion vote's answer, DealHunt's `{ helpful_count, mine }` (contract §4), as the
 * `{ helpful, mine }` this module reads; anything else is returned untouched — and then fails
 * `isHelpfulAnswer`, so the press rolls back like any non-answer.
 */
export function fromDiscussionAnswer(body: unknown): unknown {
  const v = body as { helpful_count?: unknown; mine?: unknown } | null
  return v && typeof v === 'object' && 'helpful_count' in v
    ? { helpful: v.helpful_count, mine: v.mine }
    : body
}

/** This post's entry in a `helpful/me?ids=` body (`{ mine: Record<id, boolean> }`), or null when
 *  the body says nothing about it — unknown, never "not pressed". */
export function discussionMine(body: unknown, id: string): boolean | null {
  const mine = (body as { mine?: unknown } | null)?.mine
  if (typeof mine !== 'object' || mine === null || Array.isArray(mine)) {
    return null
  }
  const value = (mine as Record<string, unknown>)[id]
  return typeof value === 'boolean' ? value : null
}

/**
 * The keyed read of the reader's own vote when it carries no count (a discussion's
 * `helpful/me`): `mine` becomes known and the count stays the public one — never a count built
 * from a stale figure. Like `helpfulRead`, ignored once a vote answered or while one is in flight;
 * null (DealHunt said nothing) leaves the vote unknown.
 */
export function helpfulReadMine(state: HelpfulState, mine: boolean | null): HelpfulState {
  if (state.settled || state.busy || mine === null) {
    return state
  }
  const marked = !state.own && mine
  return { ...state, mine: marked, value: marked ? 'yes' : null, known: true }
}

/**
 * The reader wrote this review: the signed-in author by handle (handles are unique among claimed
 * profiles), or the device that wrote it, signed in or not. The one rule behind "This is your
 * review" and the Helpful control's own-review state.
 */
export function isOwnReview(
  me: {
    profile: { handle: string; claimed: boolean }
    shelf: Array<{ note: { id: string } }>
  },
  review: { noteId: string; handle: string }
): boolean {
  const byHandle =
    me.profile.claimed && me.profile.handle.toLowerCase() === review.handle.toLowerCase()
  return byHandle || me.shelf.some(row => row.note.id === review.noteId)
}
